import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  Space,
  Tag,
  message,
} from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import { relatorioService, unidadeService } from '../services/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const TIPO_OPTIONS = ['TODOS', 'ENTRADA', 'SAIDA'];
const STATUS_OPTIONS = ['TODOS', 'PAGO', 'EM_ABERTO', 'VENCIDO', 'CANCELADO', 'NEGOCIADO'];

const STATUS_COLOR = {
  PAGO: 'green',
  EM_ABERTO: 'orange',
  VENCIDO: 'red',
  CANCELADO: 'default',
  NEGOCIADO: 'blue',
};

const TIPO_COLOR = {
  ENTRADA: 'green',
  SAIDA: 'red',
};

const TOTAIS_ZERO = {
  total_entradas: 0,
  total_saidas: 0,
  saldo: 0,
  quantidade: 0,
};

const EXPORT_LIMIT = 5000;

const formatMoeda = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const toArray = (responseData) => {
  if (!responseData) return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.items)) return responseData.items;
  return [];
};

const RelatorioContabil = () => {
  const [loadingData, setLoadingData] = useState(false);
  const [loadingFiltros, setLoadingFiltros] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [totais, setTotais] = useState(TOTAIS_ZERO);
  const [hasQueried, setHasQueried] = useState(false);

  const [periodo, setPeriodo] = useState(null);
  const [unidades, setUnidades] = useState([]);

  const [filtros, setFiltros] = useState({
    tipo: 'TODOS',
    status: 'TODOS',
    id_unidade: null,
  });

  const setFiltro = useCallback((key, value) => {
    setFiltros((prev) => ({ ...prev, [key]: value ?? null }));
  }, []);

  const carregarUnidades = useCallback(async () => {
    setLoadingFiltros(true);
    try {
      const response = await unidadeService.list();
      setUnidades(toArray(response?.data));
    } catch {
      message.error('Erro ao carregar unidades.');
    } finally {
      setLoadingFiltros(false);
    }
  }, []);

  React.useEffect(() => {
    carregarUnidades();
  }, [carregarUnidades]);

  const buildParams = useCallback(
    (limit) => {
      const [dataInicio, dataFim] = periodo || [];
      const params = {
        data_inicio: dataInicio.format('YYYY-MM-DD'),
        data_fim: dataFim.format('YYYY-MM-DD'),
        tipo: filtros.tipo,
        status: filtros.status,
        limit,
      };
      if (filtros.id_unidade) {
        params.id_unidade = filtros.id_unidade;
      }
      return params;
    },
    [periodo, filtros]
  );

  const periodoValido = Array.isArray(periodo) && periodo[0] && periodo[1];

  // ── Busca do relatório ──────────────────────────────────────────
  const fetchRelatorio = useCallback(async () => {
    if (!periodoValido) {
      message.warning('Selecione o período (data inicial e data final).');
      return;
    }
    setLoadingData(true);
    try {
      const params = buildParams(1000);
      const response = await relatorioService.contabeis(params);
      const payload = response?.data ?? {};
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotais(payload.totais ?? TOTAIS_ZERO);
      setHasQueried(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = detail
        ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
        : 'Erro ao gerar relatório contábil.';
      message.error(msg);
    } finally {
      setLoadingData(false);
    }
  }, [periodoValido, buildParams]);

  // ── Exportação Excel (busca novamente, com limit máximo) ─────────
  const exportarExcel = useCallback(async () => {
    if (!periodoValido) {
      message.warning('Selecione o período (data inicial e data final).');
      return;
    }
    setExportLoading(true);
    try {
      const params = buildParams(EXPORT_LIMIT);
      const response = await relatorioService.contabeis(params);
      const payload = response?.data ?? {};
      const registros = Array.isArray(payload.items) ? payload.items : [];

      if (registros.length === 0) {
        message.warning('Nenhum dado para exportar.');
        return;
      }

      const linhas = registros.map((item) => ({
        Data: item.data ? dayjs(item.data).format('DD/MM/YYYY') : '',
        Tipo: item.tipo ?? '',
        Origem: item.origem ?? '',
        Descrição: item.descricao ?? '',
        Pessoa: item.pessoa ?? '',
        Unidade: item.unidade ?? '',
        Categoria: item.categoria ?? '',
        Valor: Number(item.valor ?? 0),
        Status: item.status ?? '',
        Competência: item.competencia ?? '',
      }));

      const planilha = XLSX.utils.json_to_sheet(linhas);
      const livro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(livro, planilha, 'Relatorio Contabil');
      XLSX.writeFile(livro, `relatorios-contabeis-${dayjs().format('YYYY-MM-DD')}.xlsx`);

      message.success(`Excel exportado com ${registros.length} registro(s).`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = detail
        ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
        : 'Erro ao exportar relatório contábil.';
      message.error(msg);
    } finally {
      setExportLoading(false);
    }
  }, [periodoValido, buildParams]);

  // ── Colunas da tabela ───────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        title: 'Data',
        dataIndex: 'data',
        key: 'data',
        width: 110,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => (a.data > b.data ? 1 : -1),
        render: (val) => (val ? dayjs(val).format('DD/MM/YYYY') : '-'),
      },
      {
        title: 'Tipo',
        dataIndex: 'tipo',
        key: 'tipo',
        width: 100,
        align: 'center',
        render: (val) => (val ? <Tag color={TIPO_COLOR[val] ?? 'default'}>{val}</Tag> : '-'),
      },
      {
        title: 'Origem',
        dataIndex: 'origem',
        key: 'origem',
        width: 100,
      },
      {
        title: 'Descrição',
        dataIndex: 'descricao',
        key: 'descricao',
        ellipsis: true,
      },
      {
        title: 'Pessoa',
        dataIndex: 'pessoa',
        key: 'pessoa',
        width: 160,
        ellipsis: true,
        render: (val) => val ?? '-',
      },
      {
        title: 'Unidade',
        dataIndex: 'unidade',
        key: 'unidade',
        width: 150,
        ellipsis: true,
        render: (val) => val ?? '-',
      },
      {
        title: 'Categoria',
        dataIndex: 'categoria',
        key: 'categoria',
        width: 150,
        ellipsis: true,
        render: (val) => val ?? '-',
      },
      {
        title: 'Valor',
        dataIndex: 'valor',
        key: 'valor',
        width: 130,
        align: 'right',
        render: (val) => formatMoeda(val),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        align: 'center',
        render: (val) => (val ? <Tag color={STATUS_COLOR[val] ?? 'default'}>{val}</Tag> : '-'),
      },
      {
        title: 'Competência',
        dataIndex: 'competencia',
        key: 'competencia',
        width: 110,
        render: (val) => val ?? '-',
      },
    ],
    []
  );

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Relatórios Contábeis</Title>

      {/* ── Filtros ─────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 4 }}>Período</div>
            <RangePicker
              value={periodo}
              onChange={(datas) => setPeriodo(datas)}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4 }}>Tipo</div>
            <Select
              value={filtros.tipo}
              onChange={(v) => setFiltro('tipo', v)}
              style={{ width: '100%' }}
              options={TIPO_OPTIONS.map((t) => ({ label: t, value: t }))}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4 }}>Status</div>
            <Select
              value={filtros.status}
              onChange={(v) => setFiltro('status', v)}
              style={{ width: '100%' }}
              options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <div style={{ marginBottom: 4 }}>Unidade</div>
            <Select
              allowClear
              placeholder="Todas"
              value={filtros.id_unidade}
              onChange={(v) => setFiltro('id_unidade', v)}
              loading={loadingFiltros}
              style={{ width: '100%' }}
              options={unidades.map((u) => ({
                label: u.nome_fantasia || u.nome,
                value: u.id,
              }))}
            />
          </Col>

          <Col xs={24} sm={24} md={6}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={fetchRelatorio}
                loading={loadingData}
              >
                Buscar
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportarExcel}
                loading={exportLoading}
                disabled={!hasQueried || items.length === 0}
              >
                Exportar Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ── Cards de totais ─────────────────────────────────────── */}
      {hasQueried && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Entradas"
                value={formatMoeda(totais.total_entradas)}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Saídas"
                value={formatMoeda(totais.total_saidas)}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Saldo"
                value={formatMoeda(totais.saldo)}
                valueStyle={{ color: totais.saldo >= 0 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="Quantidade" value={totais.quantidade} />
            </Card>
          </Col>
        </Row>
      )}

      {/* ── Tabela ──────────────────────────────────────────────── */}
      <Table
        rowKey={(record, index) => `${record.origem}-${record.data}-${index}`}
        columns={columns}
        dataSource={items}
        loading={loadingData}
        bordered
        scroll={{ x: 'max-content' }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (total) => `${total} registro(s)`,
        }}
        locale={{
          emptyText: hasQueried
            ? 'Nenhum registro encontrado para os filtros selecionados.'
            : 'Selecione o período e clique em Buscar para carregar o relatório.',
        }}
      />
    </div>
  );
};

export default RelatorioContabil;
